
import { Component, Prop, Vue, Watch, } from 'vue-property-decorator';
import { Validation, validationMixin, } from 'vuelidate';
import { email, minLength, required, } from 'vuelidate/lib/validators';
import { isBadRequestResult, } from '~/api/error-helpers';
import { Store, } from '~/apps/account/store';
import FeedbackBox from '~/components/feedback-box/feedback-box.vue';
import HeaderText from '~/components/header-text/header-text.vue';
import LandingLink from '~/components/landing-link/landing-link.vue';
import PasswordInput from '~/components/self-serve/password-input/password-input.vue';
import type { SapInvalidDataResponse } from '~/api/self-serve-client';

@Component({
	components: {
		FeedbackBox,
		HeaderText,
		LandingLink,
		PasswordInput,
	},
	mixins: [validationMixin,],
	validations() {
		return {
			customerEmail: {
				required,
				email,
			},
			customerPassword: {
				required,
				minLength: minLength(2),
			},
		};
	},
})
export default class LoginForm extends Vue {
	apiError: string | null = null;
	customerPassword = '';
	customerEmail = '';
	invalidCredentials = false;
	passwordVisible = false;
	accountDisabledError = false;
	SouthEastWaterCustomer = false;

	@Watch('$v.$anyError')
	onError() {
		Store.commit.clearReadMessage();
	}

	@Prop({
		type: String,
		required: true,
	})
	contactUsLocation!: string;

	async handleSubmit() {
		Store.commit.clearReadMessage();
		const currentForm = this.$v;
		currentForm.$touch();
		window.scrollTo(0, 0);
		this.accountDisabledError = false;
		if (currentForm.$pending || currentForm.$error) {
			return;
		}

		const { customerEmail, customerPassword, } = this;
		Store.commit.showSpinner();
		try {
			await Store.dispatch.authenticate({
				emailAddress: customerEmail,
				password: customerPassword,
			});
			// Change the implementation as we have to handle the 599 error for api/business-partner/:number API and display the custom error message on the login screen,
			// due to which we have to remove the below dispatch method from account store from authenticate method(called in the end) and added here
				try {
					await this.initiateIdentifyCustomer();
				} catch (error) {
					return;
				}
			
			const redirectPath = Store.state.postLoginPath || this.postLoginRedirection();
			Store.commit.setPostLoginPath(null);
			void this.$router
				.push(redirectPath ?? { name: 'Dashboard', })
				.catch((e) => console.warn(e));
		} catch (error) {
			let apiError = `Please wait a few minutes and try again. If the problem persists, please <a target="_blank" href=${this.contactUsLocation}>contact us</a>.`;
			if (isBadRequestResult(error) &&
				Array.isArray(error.errors) &&
				error.errors[0]?.message) {
				if (error.errors[0]?.metadata?.Number === '009') {
					this.accountDisabledError = true;
					return;
				} else if (error.errors[0]?.metadata?.Number === '015') {
					this.SouthEastWaterCustomer = true;
					apiError = `We've identified you as a South East Water customer and have removed your access as we no longer manage your billing for wastewater services.<br><br>Please <a href='https://www.southeastwater.co.uk/help/contact' target='_blank'> get in touch </a> with South East Water to manage your account.`;
				} else {
					this.invalidCredentials = error.errors[0]?.metadata?.Number === '008';
					apiError = error.errors[0].message;
				}
			}
			this.apiError = apiError;
		} finally {
			Store.commit.hideSpinner();
		}
	}

	async initiateIdentifyCustomer() {
		try {
			await Store.dispatch.identifyCustomer({ fetchTime: false, journeyName: 'Login', });
		} catch (error) {
			let apiError = `Please wait a few minutes and try again. If the problem persists, please <a target="_blank" href=${this.contactUsLocation}>contact us</a>.`;
			
			const { status, type, reason } = error as SapInvalidDataResponse;
			if (status === 599 && 
				type === 'Contract' && 
				reason === 'Multiple meters found') {
				apiError = `Sorry, we’re unable to complete your request. To access your account, please get in touch and let us know this error code: ${status}.`;
			}
			
			this.apiError = apiError;
			throw error;
		}
	}

	postLoginRedirection() {
		if (this.$route.query.redirectUrl) {
			return this.$route.query.redirectUrl as string;
		}
	}

	togglePasswordVisibility() {
		this.passwordVisible = !this.passwordVisible;
	}

	touchField(field: Validation) {
		field.$touch();
		if (this.invalidCredentials) {
			if (field.$invalid) {
				this.apiError = null;
			}
			this.invalidCredentials = false;
		}
	}

	focusRef(ref: string) {
		(this.$refs[ref] as HTMLElement)?.focus();
	}

	setFocusOnPassword() {
		((this.$refs.passwordControl as Vue).$refs.passwordInput as Element as HTMLElement).focus();
	}
}
